<script setup lang="ts">
import { ref, onBeforeUnmount, onMounted, nextTick } from 'vue';
import 'ckeditor5/ckeditor5.css';
import {
  AutoLink,
  Autoformat,
  BalloonEditor,
  Base64UploadAdapter,
  BlockToolbar,
  Bold,
  ClassicEditor,
  Essentials,
  Font,
  Heading,
  Highlight,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
} from 'ckeditor5';
import { useDebounceFn } from '@vueuse/core';
import { logger } from '@/utils/logger';

const customColorPalette = [
  {
    color: 'hsl(4, 90%, 58%)',
    label: 'Red',
  },
  {
    color: 'hsl(340, 82%, 52%)',
    label: 'Pink',
  },
  {
    color: 'hsl(291, 64%, 42%)',
    label: 'Purple',
  },
  {
    color: 'hsl(262, 52%, 47%)',
    label: 'Deep Purple',
  },
  {
    color: 'hsl(231, 48%, 48%)',
    label: 'Indigo',
  },
  {
    color: 'hsl(207, 90%, 54%)',
    label: 'Blue',
  },

  // More colors.
  // ...
];

const props = defineProps({
  extendedMode: {
    type: Boolean,
    default: false,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  readOnlyMode: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  editorType: {
    type: String,
    default: 'classic',
  },
  placeholder: {
    type: String,
    default: 'Type the content here!',
  },
  initialStyling: {
    type: String,
    default: 'paragraph',
  },
});

const textValue = defineModel({
  type: String,
  required: true,
});

const editor = ref(props.editorType === 'classic' ? ClassicEditor : BalloonEditor);
const editorInstance = ref();
const isEditorMounted = ref(false);
/**
 * Held at setup level so its cleanup can be registered at setup level too: the
 * observer is created inside a timeout, and a lifecycle hook registered from
 * there has no component instance to attach to — Vue warns and drops it, which
 * left one observer alive per editor mount.
 */
const resizeObserver = ref<ResizeObserver | null>(null);
const editorContainer = ref<HTMLElement | null>(null);

const editorConfig = {
  plugins: [
    Autoformat,
    BlockToolbar,
    Base64UploadAdapter,
    PasteFromOffice,
    Essentials,
    Bold,
    Italic,
    AutoLink,
    Link,
    Paragraph,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    Font,
    Image,
    ImageUpload,
    ImageToolbar,
    ImageResize,
    ImageCaption,
    ImageStyle,
    List,
    Heading,
    Highlight,
    SelectAll,
  ],
  placeholder: props.placeholder,
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'bold',
      'italic',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      // fonts
      props.extendedMode ? '|' : '',
      props.extendedMode ? 'fontColor' : '',
      props.extendedMode ? 'fontBackgroundColor' : '',
      props.extendedMode ? '|' : '',
    ],
  },
  link: {
    // Automatically add target="_blank" and rel="noopener noreferrer" to all external links.
    addTargetToExternalLinks: true,
  },
  fontSize: {
    options: [9, 11, 13, 'default', 15, 16, 17, 18, 19, 20, 21],
  },
  heading: {
    options: [
      { model: 'paragraph' as const, title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1' as const, view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2' as const, view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3' as const, view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4' as const, view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5' as const, view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
    ],
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
    ],
    // Set the palettes for tables.
    tableProperties: {
      borderColors: customColorPalette,
      backgroundColors: customColorPalette,
    },

    // Set the palettes for table cells.
    tableCellProperties: {
      borderColors: customColorPalette,
      backgroundColors: customColorPalette,
    },
  },
  licenseKey: 'GPL',
  height: '200px',
  width: '100%',
};

function resizeEditor(): void {
  if (!editorInstance.value || !editorContainer.value) return;

  try {
    // Get container width
    const containerWidth = editorContainer.value.clientWidth;

    // Apply width to editor
    const editorElement = editorInstance.value.ui.view.element;
    if (editorElement) {
      editorElement.style.width = `${containerWidth}px`;
    }

    // Apply width to content
    const editorContentElement = editorInstance.value.ui.view.editable.element;
    if (editorContentElement) {
      editorContentElement.style.width = `${containerWidth}px`;
    }

    // Apply width to toolbar
    const toolbarElement = editorInstance.value.ui.view.toolbar.element;
    if (toolbarElement) {
      toolbarElement.style.width = `${containerWidth}px`;
    }
  } catch (error) {
    logger.error('Error resizing editor width:', error);
  }
}

// Create a delayed mounting sequence with multiple checks
onMounted(() => {
  // First delay - wait for component mount
  setTimeout(() => {
    // Second delay - wait for DOM update
    void nextTick(() => {
      isEditorMounted.value = true;

      // Third delay - wait for editor mount and gather initial dimensions
      setTimeout(() => {
        if (editorInstance.value && editorContainer.value) {
          try {
            // Set height
            // @ts-expect-error editor instance methods not typed
            editorInstance.value.editing.view.change(writer => {
              writer.setAttribute(
                'style',
                'height: 200px; width: 100%',
                editorInstance.value.editing.view.document.getRoot()
              );
            });

            // Force width adjustment
            resizeEditor();

            // Watch the container so the editor can follow its width.
            resizeObserver.value?.disconnect();
            resizeObserver.value = new ResizeObserver(() => {
              resizeEditor();
            });
            resizeObserver.value.observe(editorContainer.value);
          } catch (error) {
            logger.error('Error initializing editor:', error);
          }
        }
      }, 300);
    });
  }, 100);
});

function onEditorReady(editor: unknown): void {
  editorInstance.value = editor;

  // Force dimensions on initialization
  try {
    // @ts-expect-error editor is not typed
    const element = editor.ui.getEditableElement();
    if (element) {
      element.style.height = '200px';
      element.style.minHeight = '200px';
      element.style.width = '100%';
      element.style.overflow = 'auto';
    }

    // Force a resize after editor is ready
    setTimeout(resizeEditor, 100);
  } catch (error) {
    logger.error('Error setting editor dimensions:', error);
  }

  if (props.autoFocus) {
    // @ts-expect-error editor is not typed
    editor.model.change((writer: unknown) => {
      // @ts-expect-error writer is not typed
      writer.setSelection(editor.model.document.getRoot(), 'end');
    });
    // @ts-expect-error editor is not typed
    editor.editing.view.focus();
  }
  if (props.initialStyling) {
    // @ts-expect-error editor is not typed
    editor.execute('heading', { value: props.initialStyling });
  }
}

const emit = defineEmits(['input-blurred', 'editor-unmounted', 'editor-save-interval']);

const intervalID = ref();

// logic: on-focus start the interval, on-blur clear the interval
// on-clear save the content
// on-unmount save the content with versioning

function onEditorFocus(): void {
  if (!props.editMode) return;
  clearSelection();
  intervalID.value = setInterval(autoSaveContent, 10000);
}

function onEditorBlur(): void {
  if (!props.editMode || hasHtmlMarkTag(textValue.value)) {
    // if there is a selected text, it's value shouldn't be saved
    // if the user writes, and highlights in the same time, the value will not be saved, until unmounted
    // could be caught in interval...
    return;
  }
  emit('input-blurred', editorInstance.value.getData());
  clearInterval(intervalID.value);
}

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect();
  resizeObserver.value = null;
});

onBeforeUnmount(() => {
  if (!props.editMode) return;
  clearInterval(intervalID.value);
  // save WITH versioning
  clearSelection();
  emit('editor-unmounted', editorInstance.value.getData());
});

function autoSaveContent(): void {
  if (!props.editMode || hasHtmlMarkTag(textValue.value)) {
    return;
  }
  emit('editor-save-interval', textValue.value);
}

const onEditorInput = useDebounceFn(() => {
  if (!props.editMode) return;
}, 500);

function clearSelection(): void {
  if (!editorInstance.value) return;
  if (hasHtmlMarkTag(editorInstance.value.getData())) {
    editorInstance.value.execute('selectAll');
    editorInstance.value.execute('highlight', { value: null });
  }
}

function hasHtmlMarkTag(str: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  return doc.querySelectorAll('mark').length > 0;
}
</script>

<template>
  <div ref="editorContainer" class="ckeditor-wrapper">
    <ckeditor
      v-if="isEditorMounted"
      v-model="textValue"
      :editor="editor"
      :config="editorConfig"
      :disabled="readOnlyMode"
      @ready="onEditorReady"
      @blur="onEditorBlur"
      @focus="onEditorFocus"
      @input="onEditorInput"
    />
  </div>
</template>

<style>
.ckeditor-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Target the editor */
:deep(.ck-editor) {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  min-width: 0 !important; /* Prevent overflow */
}

/* Target the content container */
:deep(.ck-editor__main) {
  flex: 1 !important;
  min-height: 175px !important;
  width: 100% !important;
  min-width: 0 !important; /* Prevent overflow */
}

/* Target the editable area */
:deep(.ck-editor__editable) {
  min-height: 175px !important;
  height: 200px !important;
  overflow-y: auto !important;
  width: 100% !important;
  min-width: 0 !important; /* Prevent overflow */
}

/* Ensure editor toolbar is visible and full width */
:deep(.ck-toolbar) {
  z-index: 1 !important;
  width: 100% !important;
  min-width: 0 !important; /* Prevent overflow */
}

.ck-content .marker-purple {
  background-color: #f4e0f3;
}
</style>
